import {
  User,
  Sparkles,
  FileText,
  Search,
  Package,
  Check,
  Circle,
  Loader2,
  Zap,
  MessageCircle,
  type LucideIcon,
} from 'lucide-react';
import type { PipelineStep, StudioPanelId, PipelineStepStatus } from '../types';

const stepIcons: Record<string, LucideIcon> = {
  brief: User,
  angles: MessageCircle, // Hook icon - emphasizing the hook concept
  script: FileText,
  review: Search,
  assets: Zap, // More action-oriented for ad pack
};

interface PipelineBarProps {
  steps: PipelineStep[];
  activePanel: StudioPanelId;
  onStepClick: (panelId: StudioPanelId) => void;
}

function StepIndicator({ status }: { status: PipelineStepStatus }) {
  switch (status) {
    case 'completed':
      return (
        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shrink-0">
          <Check size={12} className="text-white" strokeWidth={3} />
        </div>
      );
    case 'active':
      return (
        <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
          <Loader2 size={12} className="text-white animate-spin" />
        </div>
      );
    default:
      return (
        <div className="w-5 h-5 rounded-full border-2 border-neutral-300 flex items-center justify-center shrink-0">
          <Circle size={6} className="text-neutral-300" />
        </div>
      );
  }
}

export function PipelineBar({ steps, activePanel, onStepClick }: PipelineBarProps) {
  return (
    <div className="flex border-b border-neutral-200 bg-white px-3 gap-0.5 overflow-x-auto">
      {steps.map((step, i) => {
        const Icon = stepIcons[step.id] || Circle;
        const isActive = step.id === activePanel;
        return (
          <button
            key={step.id}
            onClick={() => onStepClick(step.id)}
            className={`
              flex items-center gap-2 px-4 py-3 border-b-2 transition-colors text-sm whitespace-nowrap
              ${isActive
                ? 'border-primary-500 text-primary-600 font-semibold'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
              }
              ${step.status === 'completed' && !isActive ? 'text-green-600' : ''}
            `}
          >
            <StepIndicator status={isActive ? 'active' : step.status} />
            <Icon size={14} />
            <span>{step.label}</span>
            {step.summary && (
              <span className="text-xs text-neutral-400 ml-1 hidden sm:inline">
                {step.summary}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
